import { useCallback, useEffect, useState } from 'react';

import EmailIcon from 'icons/email.svg?react';
import { toast } from 'material-react-toastify';

import Popup from '@/components/Popup/PopUp/PopUp';
import Button from '@/components/shared/Button/Button';
import { Text } from '@/components/shared/Text/Text';
import { TextField } from '@/components/shared/TextField/TextField';
import { useContentViewStore } from '@/lib/store/contentViewStore';

import { useContentQueries } from '../../services/ContentManagerService';

interface CopyBlockModalProps {
  isPopupOpen: boolean;
  closePopup: () => void;
}

const CopyBlockModal = ({ isPopupOpen, closePopup }: CopyBlockModalProps) => {
  const copyBlockSlug = useContentViewStore((state) => state.copyBlockSlug);
  const { useCopyBlock } = useContentQueries();
  const copyBlockQuery = useCopyBlock();

  const [newSlug, setNewSlug] = useState<string>(copyBlockSlug || '');

  const sanitizeSlug = useCallback((value: string) => value.replace(/\s+/g, ''), []);

  const handleSlugChange = (value: string) => {
    setNewSlug(sanitizeSlug(value));
  };

  const handleCopyBlock = async () => {
    try {
      await copyBlockQuery.mutateAsync({ slug: copyBlockSlug || '', newSlug: newSlug });
      toast.success('Block copied successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to copy block');
    } finally {
      closePopup();
    }
  };

  useEffect(() => {
    setNewSlug(sanitizeSlug(copyBlockSlug || ''));
  }, [copyBlockSlug, sanitizeSlug]);

  return (
    <Popup isOpen={isPopupOpen} handleClose={closePopup}>
      <div className="absolute top-[20px] left-[12px] rounded-lg bg-[#F2F2F2] p-2">
        <EmailIcon />
      </div>
      <div className="max-w-[340px] ml-12">
        <Text.Bigger className="mb-2" weight="bold">
          Copy block
        </Text.Bigger>
        <Text.Regular>Please enter the new slug for the block in the field below:</Text.Regular>
        <TextField
          startIcon={<EmailIcon className="opacity-25" />}
          className="my-4"
          value={newSlug}
          onChange={(e) => handleSlugChange(e.target.value)}
          disabled={copyBlockQuery.isPending}
        />

        <div className="flex justify-center items-center gap-2">
          <Button
            label={copyBlockQuery.isPending ? 'Copying...' : 'Copy Block'}
            size="s"
            className="!w-44"
            onClick={handleCopyBlock}
            prefixIcon={<img alt={'check'} src={'/icons/check.svg'} />}
            disabled={copyBlockQuery.isPending || newSlug === copyBlockSlug || !newSlug}
          />
          <Button
            prefixIcon={<img alt={'close'} src={'/icons/close_outline.svg'} />}
            label="Cancel"
            variant="transparent"
            onClick={closePopup}
            className="!w-28"
            size="s"
            disabled={copyBlockQuery.isPending}
          />
        </div>
      </div>
    </Popup>
  );
};

export default CopyBlockModal;
